import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, RefreshCcw, RotateCw } from "lucide-react";
import {
  getCfoAssessment,
  getCfoScoreExplanation,
  requestCfoEvaluation,
} from "@/api/cfoWorkflow";
import type { CfoScoreExplanation } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CfoScoreCard } from "./CfoScoreCard";

const STALE: Record<string, string> = {
  CONFIGURATION_CHANGED: "The scoring configuration changed since this evaluation.",
  EXPIRED: "This evaluation has expired.",
  INVALIDATED: "An input changed since this evaluation; it is being re-evaluated.",
  SUPERSEDED: "A newer evaluation exists.",
};

/** The CFO score of a company, rule by rule, with its evidence. */
export function CfoAssessmentPanel({
  slug,
  companyId,
  canRequest,
}: {
  slug: string;
  companyId: number;
  canRequest: boolean;
}) {
  const [explanation, setExplanation] = useState<CfoScoreExplanation | null>(null);
  const [stale, setStale] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "none" | "error">("loading");
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let live = true;
    Promise.all([getCfoScoreExplanation(slug, companyId), getCfoAssessment(slug, companyId)])
      .then(([value, assessment]) => {
        if (!live) return;
        setExplanation(value);
        setStale(assessment.stale_reason ?? null);
        setState("ready");
      })
      .catch((error: unknown) => {
        if (!live) return;
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        setState(status === 404 ? "none" : "error");
      });
    return () => {
      live = false;
    };
  }, [slug, companyId, revision]);

  async function reevaluate() {
    setBusy(true);
    try {
      const asOf = new Date().toISOString().slice(0, 10);
      await requestCfoEvaluation(slug, companyId, { as_of: asOf, cycle_key: `manual:${asOf}` });
      toast.success("Re-evaluation requested", {
        description: "Refresh in a few seconds to see the new score.",
      });
    } catch {
      toast.error("Re-evaluation not authorized or company not admitted.");
    } finally {
      setBusy(false);
    }
  }

  const actions = (
    <div className="flex items-center gap-0.5">
      {canRequest && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-slate-500"
          disabled={busy}
          onClick={() => void reevaluate()}
          title="Request a local re-evaluation"
        >
          <RotateCw className={cn(busy && "animate-spin")} />
          Re-evaluate
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-slate-500"
        aria-label="Refresh the score"
        title="Refresh the score"
        onClick={() => setRevision((v) => v + 1)}
      >
        <RefreshCcw />
      </Button>
    </div>
  );

  if (state === "loading") {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading the CFO score">
        <div className="h-3 w-40 animate-pulse rounded bg-slate-100" />
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
            <div className="h-1.5 w-full animate-pulse rounded-full bg-slate-100" />
          </div>
        ))}
      </div>
    );
  }
  if (state !== "ready" || !explanation) {
    return (
      <div className="flex items-center justify-between gap-3 text-sm text-slate-500">
        <span>
          {state === "none"
            ? "No CFO evaluation yet."
            : "CFO evaluation unavailable or company not admitted."}
        </span>
        {actions}
      </div>
    );
  }
  return (
    <CfoScoreCard
      explanation={explanation}
      actions={actions}
      notice={
        stale ? (
          <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{STALE[stale] ?? `This evaluation is not current (${stale}).`}</span>
          </div>
        ) : undefined
      }
    />
  );
}
